import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';

interface ClusteringRadarChartProps {
    data: Record<string, number>;
    allClustersData: Record<number, Record<string, number>>; // All clusters for normalization
    asset: string; // Asset name to filter features
    size?: number;
}

export const ClusteringRadarChart: React.FC<ClusteringRadarChartProps> = ({
    data,
    allClustersData,
    asset,
    size = 120
}) => {
    const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);

    // Filter features that contain the asset name, exclude cluster_id, and sort alphabetically
    const assetFeatures = Object.entries(data)
        .filter(([key]) =>
            key.toLowerCase().includes(asset.toLowerCase()) &&
            key !== 'cluster_id'
        )
        .sort(([a], [b]) => a.localeCompare(b)); // Sort alphabetically by feature key

    if (assetFeatures.length === 0) return null;

    // Get global min/max for each feature across all clusters for normalization
    const globalStats = assetFeatures.reduce((acc, [featureKey]) => {
        const allValues = Object.values(allClustersData)
            .map(clusterData => clusterData[featureKey])
            .filter(val => val !== undefined && !isNaN(val));

        if (allValues.length > 0) {
            acc[featureKey] = {
                min: Math.min(...allValues),
                max: Math.max(...allValues)
            };
        }
        return acc;
    }, {} as Record<string, { min: number; max: number }>);

    // Normalize data using global stats
    const normalizedData = assetFeatures.map(([key, value]) => {
        const stats = globalStats[key];
        const range = stats.max - stats.min;
        const normalizedValue = range === 0 ? 0.5 : (value - stats.min) / range;

        // Show complete feature name, just clean up underscores
        const displayName = key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words

        return {
            key,
            displayName,
            value: Math.max(0, Math.min(1, normalizedValue)), // Clamp between 0-1
            rawValue: value
        };
    });

    const centerX = size / 2;
    const centerY = size / 2;
    const innerRadius = size * 0.15;
    const outerRadius = size * 0.45;
    const angleStep = (2 * Math.PI) / normalizedData.length;

    // Create polygon points
    const points = normalizedData.map((item, index) => {
        const angle = index * angleStep - Math.PI / 2;
        const radius = innerRadius + (outerRadius - innerRadius) * item.value;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        return `${x},${y}`;
    }).join(' ');

    // Create background reference circle
    const backgroundPoints = normalizedData.map((_, index) => {
        const angle = index * angleStep - Math.PI / 2;
        const x = centerX + Math.cos(angle) * outerRadius;
        const y = centerY + Math.sin(angle) * outerRadius;
        return `${x},${y}`;
    }).join(' ');

    // Create axis lines (without labels)
    const axisElements = normalizedData.map((item, index) => {
        const angle = index * angleStep - Math.PI / 2;
        const lineEndX = centerX + Math.cos(angle) * outerRadius;
        const lineEndY = centerY + Math.sin(angle) * outerRadius;

        return (
            <line
                key={item.key}
                x1={centerX}
                y1={centerY}
                x2={lineEndX}
                y2={lineEndY}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth="0.5"
            />
        );
    });

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <svg width={size} height={size}>
                {/* Background reference polygon */}
                <polygon
                    points={backgroundPoints}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.1)"
                    strokeWidth="1"
                />

                {/* Inner circle for reference */}
                <circle
                    cx={centerX}
                    cy={centerY}
                    r={innerRadius}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.05)"
                    strokeWidth="1"
                />

                {/* Axis lines */}
                {axisElements}

                {/* Data polygon */}
                <polygon
                    points={points}
                    fill="rgba(144, 202, 249, 0.2)"
                    stroke="#90CAF9"
                    strokeWidth="1.5"
                />

                {/* Data points with hover */}
                {normalizedData.map((item, index) => {
                    const angle = index * angleStep - Math.PI / 2;
                    const radius = innerRadius + (outerRadius - innerRadius) * item.value;
                    const x = centerX + Math.cos(angle) * radius;
                    const y = centerY + Math.sin(angle) * radius;

                    return (
                        <Tooltip
                            key={item.key}
                            title={`${item.displayName}: ${item.rawValue.toFixed(4)}`}
                            placement="top"
                            arrow
                        >
                            <circle
                                cx={x}
                                cy={y}
                                r="3"
                                fill="#90CAF9"
                                stroke="#ffffff"
                                strokeWidth="1"
                                style={{
                                    cursor: 'pointer',
                                    filter: hoveredFeature === item.key ? 'brightness(1.3)' : 'none'
                                }}
                                onMouseEnter={() => setHoveredFeature(item.key)}
                                onMouseLeave={() => setHoveredFeature(null)}
                            />
                        </Tooltip>
                    );
                })}

                {/* Center point */}
                <circle
                    cx={centerX}
                    cy={centerY}
                    r="1"
                    fill="rgba(255, 255, 255, 0.3)"
                />
            </svg>
        </Box>
    );
};